import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatDateForContract } from "@/shared/utils";
import type { WorkerListItem } from "@/features/hr/employees";
import type { CreateLeaveFormValues } from "../validation";
import LeavePreviewLayout from "./LeavePreviewLayout";

interface Props {
  employee: WorkerListItem;
  formData: CreateLeaveFormValues;
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  isPreviewOnly?: boolean;
}

export default function UnpaidLeavePreview({
  employee,
  formData,
  onClose,
  onSubmit,
  isLoading,
  isPreviewOnly = false,
}: Props) {
  const { t, i18n } = useTranslation("LeaveApplicationsPage");

  const dateLocale = i18n.language === "kk" ? "kk" : "ru";

  const startDateText = useMemo(
    function () {
      if (!formData.start_date) return "_____________";
      return formatDateForContract(formData.start_date, dateLocale);
    },
    [formData.start_date, dateLocale],
  );

  const endDateText = useMemo(
    function () {
      if (!formData.end_date) return "_____________";
      return formatDateForContract(formData.end_date, dateLocale);
    },
    [formData.end_date, dateLocale],
  );

  const reason = formData.reason || "_____________";

  return (
    <LeavePreviewLayout
      employee={employee}
      formData={formData}
      onClose={onClose}
      onSubmit={onSubmit}
      isLoading={isLoading}
      isPreviewOnly={isPreviewOnly}
      recipientLabel={t("preview.unpaid.toDirector")}
      fromLabel={t("preview.unpaid.from")}
      applicationTitle={t("preview.unpaid.application")}
      signatureLabel={t("preview.unpaid.signature")}
    >
      <p>
        {t("preview.unpaid.requestText")} <strong>{startDateText}</strong> {t("preview.unpaid.requestTextTo")}{" "}
        <strong>{endDateText}</strong> {t("preview.unpaid.requestTextInclusive")} <strong>{reason}</strong>.
      </p>
    </LeavePreviewLayout>
  );
}
